import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PackageCheck, Plus, Truck, X } from 'lucide-react';
import * as tienda from '@/api/tienda.api';
import type { EstadoDePago, ModoDeEnvio, Venta } from '@/api/tienda.api';
import { SeccionDetalle, DatoDetalle } from '@/components/datos/DetailDrawer';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { useToast } from '@/hooks/useToast';
import { dinero, fecha } from '@/lib/formato-tienda';

/**
 * PAGOS POR PARTES, ENTREGA Y ENVÍO — de la reunión con Tony Boga (12-oct-2026).
 * «A la medida se cobra 50 % por adelantado y el otro 50 % al retirar.»
 */

export const MODOS_DE_ENVIO: { value: ModoDeEnvio; label: string }[] = [
  { value: 'tienda', label: 'Retira en la tienda' },
  { value: 'mensajero', label: 'Mensajero' },
  { value: 'transportista', label: 'Transportista (Caribe Tours u otra)' },
];

/** La etiqueta de cómo va el pago. */
export function BadgeDePago({ estado, saldo }: { estado?: EstadoDePago; saldo?: number }) {
  if (estado === 'pagada') return <Badge variant="success">Pagada</Badge>;
  if (estado === 'anticipo') return <Badge variant="info">Abonó · debe {dinero(saldo ?? 0)}</Badge>;
  return <Badge variant="warning">Sin pago</Badge>;
}

function useInvalidar(id: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['ventas'] });
    void qc.invalidateQueries({ queryKey: ['venta', id] });
    void qc.invalidateQueries({ queryKey: ['tienda'] });
  };
}

const enteroDe = (s: string) => {
  const n = Number(s.replace(/[^\d]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

export function PagosDeLaVenta({ venta }: { venta: Venta }) {
  const toast = useToast();
  const invalidar = useInvalidar(venta.id);
  const [abriendo, setAbriendo] = useState(false);
  const [monto, setMonto] = useState('');
  const [forma, setForma] = useState(venta.formaDePago ?? '');
  const saldo = venta.saldo ?? 0;

  const pagar = useMutation({
    mutationFn: (m: number) => tienda.registrarPago(venta.id, { monto: m, forma: forma.trim() || null }),
    onSuccess: (r) => {
      toast.success(r.saldo > 0 ? `Pago anotado. Falta ${dinero(r.saldo)}.` : 'Pago anotado. Quedó pagada completa.');
      setAbriendo(false);
      setMonto('');
      invalidar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo anotar el pago'),
  });
  const quitar = useMutation({
    mutationFn: (pagoId: string) => tienda.quitarPago(venta.id, pagoId),
    onSuccess: () => {
      toast.success('Pago quitado.');
      invalidar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo quitar'),
  });

  if (venta.estado === 'ANULADA') return null;
  const pagos = venta.pagos ?? [];
  const mitad = Math.ceil(saldo / 2);

  return (
    <SeccionDetalle
      titulo="Pagos"
      accion={
        saldo > 0 &&
        !abriendo && (
          <Button size="sm" variant="outline" onClick={() => setAbriendo(true)}>
            <Plus className="h-3.5 w-3.5" />
            Anotar pago
          </Button>
        )
      }
    >
      <DatoDetalle etiqueta="Cómo va">
        <BadgeDePago estado={venta.estadoDePago} saldo={saldo} />
      </DatoDetalle>
      <DatoDetalle etiqueta="Pagado" numerico>
        {dinero(venta.pagado ?? 0)}
      </DatoDetalle>
      <DatoDetalle etiqueta="Falta" numerico>
        {dinero(saldo)}
      </DatoDetalle>

      {pagos.length > 0 && (
        <ul className="mt-2 space-y-1 border-t border-surface-border pt-2 text-sm">
          {pagos.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-2">
              <span className="text-slate-600 dark:text-slate-300">
                {fecha(p.createdAt)}
                {p.forma ? ` · ${p.forma}` : ''}
                {p.nota ? ` · ${p.nota}` : ''}
              </span>
              <span className="flex items-center gap-1 tabular-nums">
                {dinero(p.monto)}
                <button
                  type="button"
                  className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-red-600 dark:hover:bg-slate-800"
                  title="Quitar este pago (si se anotó por error)"
                  aria-label="Quitar este pago"
                  onClick={() => quitar.mutate(p.id)}
                  disabled={quitar.isPending}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      {abriendo && (
        <div className="mt-3 space-y-2 rounded-lg border border-surface-border p-3">
          <div className="flex flex-wrap gap-1.5">
            <Button size="sm" variant="outline" onClick={() => setMonto(String(mitad))}>
              La mitad ({dinero(mitad)})
            </Button>
            <Button size="sm" variant="outline" onClick={() => setMonto(String(saldo))}>
              Todo lo que falta ({dinero(saldo)})
            </Button>
          </div>
          <Input
            label="Monto"
            inputMode="numeric"
            value={monto}
            onChange={(e) => setMonto(e.target.value)}
            placeholder="2500"
          />
          <Input label="Forma de pago" value={forma} onChange={(e) => setForma(e.target.value)} placeholder="transferencia, efectivo…" />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setAbriendo(false)}>
              Cancelar
            </Button>
            <Button
              size="sm"
              disabled={enteroDe(monto) <= 0 || pagar.isPending}
              onClick={() => pagar.mutate(enteroDe(monto))}
            >
              {pagar.isPending ? 'Anotando…' : 'Anotar'}
            </Button>
          </div>
        </div>
      )}
    </SeccionDetalle>
  );
}

export function EnvioYEntrega({ venta }: { venta: Venta }) {
  const toast = useToast();
  const invalidar = useInvalidar(venta.id);
  const [editando, setEditando] = useState(false);
  const [modo, setModo] = useState<ModoDeEnvio | ''>(venta.envioModo ?? '');
  const [empresa, setEmpresa] = useState(venta.envioEmpresa ?? '');
  const [destino, setDestino] = useState(venta.envioDestino ?? '');
  const [direccion, setDireccion] = useState(venta.direccion ?? '');
  const [mensajero, setMensajero] = useState(String(venta.costoMensajero ?? 0));
  const [transporte, setTransporte] = useState(String(venta.costoTransportista ?? 0));

  const guardar = useMutation({
    mutationFn: () =>
      tienda.guardarEnvio(venta.id, {
        envioModo: modo || null,
        envioEmpresa: modo === 'transportista' ? empresa : null,
        envioDestino: destino,
        direccion,
        costoMensajero: modo === 'tienda' ? 0 : enteroDe(mensajero),
        costoTransportista: modo === 'transportista' ? enteroDe(transporte) : 0,
      }),
    onSuccess: () => {
      toast.success('Envío guardado.');
      setEditando(false);
      invalidar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo guardar'),
  });
  const entregar = useMutation({
    mutationFn: (entregada: boolean) => tienda.marcarEntregada(venta.id, entregada),
    onSuccess: (_r, entregada) => {
      toast.success(entregada ? 'Marcada como entregada.' : 'Vuelve a estar por entregar.');
      invalidar();
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : 'No se pudo'),
  });

  if (venta.estado === 'ANULADA') return null;
  const costo = (venta.costoMensajero ?? 0) + (venta.costoTransportista ?? 0);
  const etiquetaModo = MODOS_DE_ENVIO.find((m) => m.value === venta.envioModo)?.label;

  return (
    <SeccionDetalle
      titulo="Envío y entrega"
      accion={
        <div className="flex flex-wrap justify-end gap-1.5">
          {!editando && (
            <Button size="sm" variant="outline" onClick={() => setEditando(true)}>
              <Truck className="h-3.5 w-3.5" />
              {venta.envioModo ? 'Cambiar envío' : 'Anotar envío'}
            </Button>
          )}
          {venta.estado === 'CONFIRMADA' && (
            <Button
              size="sm"
              variant={venta.entregadaAt ? 'ghost' : 'outline'}
              onClick={() => entregar.mutate(!venta.entregadaAt)}
              disabled={entregar.isPending}
            >
              <PackageCheck className="h-3.5 w-3.5" />
              {venta.entregadaAt ? 'Deshacer entrega' : 'Entregada'}
            </Button>
          )}
        </div>
      }
    >
      {venta.estado === 'CONFIRMADA' && (
        <DatoDetalle etiqueta="Entrega">
          {venta.entregadaAt ? (
            <Badge variant="success">Entregada · {fecha(venta.entregadaAt)}</Badge>
          ) : (
            <Badge variant="warning">Por entregar</Badge>
          )}
        </DatoDetalle>
      )}
      {!editando && (
        <>
          <DatoDetalle etiqueta="Cómo sale">{etiquetaModo ?? '—'}</DatoDetalle>
          {venta.envioEmpresa && <DatoDetalle etiqueta="Empresa">{venta.envioEmpresa}</DatoDetalle>}
          {venta.envioDestino && <DatoDetalle etiqueta="Destino">{venta.envioDestino}</DatoDetalle>}
          {venta.direccion && <DatoDetalle etiqueta="Dirección">{venta.direccion}</DatoDetalle>}
          <DatoDetalle etiqueta="Se le cobra al cliente" numerico>
            {dinero(venta.envio)}
          </DatoDetalle>
          {costo > 0 && (
            <>
              <DatoDetalle etiqueta="Mensajero" numerico>
                {dinero(venta.costoMensajero ?? 0)}
              </DatoDetalle>
              <DatoDetalle etiqueta="Transportista" numerico>
                {dinero(venta.costoTransportista ?? 0)}
              </DatoDetalle>
              <DatoDetalle etiqueta="Diferencia" numerico>
                {dinero(venta.envio - costo)}
              </DatoDetalle>
            </>
          )}
        </>
      )}
      {editando && (
        <div className="space-y-2">
          <Select
            label="Cómo sale"
            value={modo}
            onChange={(e) => setModo(e.target.value as ModoDeEnvio | '')}
            options={[{ value: '', label: 'Sin decidir' }, ...MODOS_DE_ENVIO]}
          />
          {modo === 'transportista' && (
            <Input label="Empresa" value={empresa} onChange={(e) => setEmpresa(e.target.value)} placeholder="Caribe Tours" />
          )}
          {modo !== 'tienda' && modo !== '' && (
            <>
              <Input label="Destino" value={destino} onChange={(e) => setDestino(e.target.value)} placeholder="Santiago" />
              <Input label="Dirección" value={direccion} onChange={(e) => setDireccion(e.target.value)} />
              <Input
                label={modo === 'transportista' ? 'Mensajero hasta la empresa (RD$)' : 'Mensajero (RD$)'}
                inputMode="numeric"
                value={mensajero}
                onChange={(e) => setMensajero(e.target.value)}
              />
            </>
          )}
          {modo === 'transportista' && (
            <Input
              label="Lo que cobra la empresa (RD$)"
              inputMode="numeric"
              value={transporte}
              onChange={(e) => setTransporte(e.target.value)}
            />
          )}
          <p className="text-xs text-slate-500">
            Lo que se le cobra al cliente por el envío ({dinero(venta.envio)}) se pone al armar la venta.
          </p>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setEditando(false)}>
              Cancelar
            </Button>
            <Button size="sm" onClick={() => guardar.mutate()} disabled={guardar.isPending}>
              {guardar.isPending ? 'Guardando…' : 'Guardar'}
            </Button>
          </div>
        </div>
      )}
    </SeccionDetalle>
  );
}
